/**
 * MISSION BRIEFING modal — one-time announcement of the SHADOW NETWORK™ launch.
 * Triggers after login (when authed user is present) and shows the user's freshly
 * provisioned codename + invite link with a cinematic terminal-style reveal.
 *
 * Dismissed forever per user (localStorage `shadow_briefing_seen_{userId}`).
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Radar, ArrowRight, Crown, Copy, Check, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { trackCta, trackEvent } from "../lib/analytics";

import { API, safeJson } from "../lib/api";
// API now resolved at runtime via /lib/api.js
const BRIEF_I18N = {
  es: {
    eyebrow: "MISSION BRIEFING · 2026",
    title: "Bienvenido a la red.",
    sub: "Has sido provisionado como operador. Tu codename está activo y tu enlace de reclutamiento es operacional.",
    codename_label: "TU CODENAME",
    invite_label: "ENLACE DE RECLUTAMIENTO",
    cta_enter: "ENTRAR A LA RED",
    cta_later: "Más tarde",
    copy: "Copiar",
    copied: "Copiado",
    objectives_label: "OBJETIVOS INICIALES",
    obj_1: "Recluta 3 operadores activos → AI Mentor Boost",
    obj_2: "Completa 1 simulación → +25 XP",
    obj_3: "Sube a OPERADOR (250 XP)",
    classified: "CLASIFICADO · NIVEL OPS",
  },
  en: {
    eyebrow: "MISSION BRIEFING · 2026",
    title: "Welcome to the network.",
    sub: "You have been provisioned as an operator. Your codename is active and your recruitment link is operational.",
    codename_label: "YOUR CODENAME",
    invite_label: "RECRUITMENT LINK",
    cta_enter: "ENTER THE NETWORK",
    cta_later: "Later",
    copy: "Copy",
    copied: "Copied",
    objectives_label: "INITIAL OBJECTIVES",
    obj_1: "Recruit 3 active operators → AI Mentor Boost",
    obj_2: "Complete 1 simulation → +25 XP",
    obj_3: "Reach OPERATOR rank (250 XP)",
    classified: "CLASSIFIED · OPS LEVEL",
  },
  sk: {
    eyebrow: "MISSION BRIEFING · 2026",
    title: "Vitaj v sieti.",
    sub: "Bol si zaregistrovaný ako operátor. Tvoje codename je aktívne a tvoj náborový odkaz funkčný.",
    codename_label: "TVOJE CODENAME",
    invite_label: "NÁBOROVÝ ODKAZ",
    cta_enter: "VSTÚPIŤ DO SIETE",
    cta_later: "Neskôr",
    copy: "Kopírovať",
    copied: "Skopírované",
    objectives_label: "POČIATOČNÉ CIELE",
    obj_1: "Naverbuj 3 aktívnych operátorov → AI Mentor Boost",
    obj_2: "Dokonči 1 simuláciu → +25 XP",
    obj_3: "Dosiahni hodnosť OPERÁTOR (250 XP)",
    classified: "TAJNÉ · ÚROVEŇ OPS",
  }, de: { eyebrow: "MISSION BRIEFING · 2026", title: "Willkommen im Netzwerk.", sub: "Du wurdest als Operator bereitgestellt. Dein Codename ist aktiv und dein Rekrutierungslink ist einsatzbereit.", codename_label: "DEIN CODENAME", invite_label: "REKRUTIERUNGSLINK", cta_enter: "NETZWERK BETRETEN", cta_later: "Später", copy: "Kopieren", copied: "Kopiert", objectives_label: "ANFANGSZIELE", obj_1: "Rekrutiere 3 aktive Operatoren → AI Mentor Boost", obj_2: "Schließe 1 Simulation ab → +25 XP", obj_3: "Steige auf zu OPERATOR (250 XP)", classified: "KLASSIFIZIERT · LEVEL OPS" }, "zh-CN": { eyebrow: "任务简报 · 2026", title: "欢迎加入网络。", sub: "你已被配置为操作员。你的代号已激活，你的招募链接已可用。", codename_label: "你的代号", invite_label: "招募链接", cta_enter: "进入网络", cta_later: "稍后", copy: "复制", copied: "已复制", objectives_label: "初始目标", obj_1: "招募 3 名活跃操作员 → AI 导师加成", obj_2: "完成 1 次模拟 → +25 XP", obj_3: "升级至操作员 (250 XP)", classified: "机密 · 行动级别" },
};

export const MissionBriefingModal = () => {
  const { user, token } = useAuth();
  const { lang } = useLanguage();
  const t = BRIEF_I18N[lang] || BRIEF_I18N.en || BRIEF_I18N.es;
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [copied, setCopied] = useState(false);

  const userId = user?.id;
  const seenKey = userId ? `shadow_briefing_seen_${userId}` : null;

  // Only auto-show on the landing or Shadow Network pages — never interrupt
  // an active flow like /simulator/play, /academy, /shadow-market, etc.
  const ALLOWED_PATHS = ["/", "/shadow"];
  const isAllowedRoute = ALLOWED_PATHS.includes(location.pathname);

  // Decide whether to show. We only auto-open ONCE per (user, browser).
  useEffect(() => {
    if (!user || user === false || !token || !seenKey) return;
    if (!isAllowedRoute) return;
    let cancelled = false;
    const maybeOpen = async () => {
      try {
        const seen = localStorage.getItem(seenKey);
        if (seen) return;
      } catch { return; }
      // Fetch operator profile to populate the modal
      try {
        const res = await fetch(`${API}/shadow/me`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await safeJson(res);
        if (cancelled) return;
        setProfile(data);
        // Small UX delay so the page settles before the modal slides in
        setTimeout(() => {
          if (!cancelled) {
            setOpen(true);
            try { trackEvent("shadow_briefing_open", { codename: data.operator_codename }); } catch {}
          }
        }, 1200);
      } catch {
        /* silent */
      }
    };
    maybeOpen();
    return () => { cancelled = true; };
  }, [user, token, seenKey, isAllowedRoute]);

  // Auto-close the briefing if the user navigates away to a non-allowed route
  // (e.g. starts a simulation). Prevents the modal from hijacking other flows.
  useEffect(() => {
    if (open && !isAllowedRoute) setOpen(false);
  }, [open, isAllowedRoute]);

  const dismiss = (action = "close") => {
    try {
      if (seenKey) localStorage.setItem(seenKey, "1");
    } catch {}
    setOpen(false);
    try { trackEvent("shadow_briefing_dismiss", { action }); } catch {}
  };

  const enterNetwork = () => {
    trackCta("shadow_briefing_enter", { location: "modal" });
    dismiss("enter");
    navigate("/shadow");
  };

  const copyLink = () => {
    if (!profile?.invite_link) return;
    try {
      navigator.clipboard.writeText(profile.invite_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      trackCta("shadow_briefing_copy", { codename: profile.operator_codename });
    } catch {}
  };

  return (
    <AnimatePresence>
      {open && profile && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 p-4 backdrop-blur-xl"
          data-testid="shadow-briefing-modal"
          onClick={() => dismiss("backdrop")}
        >
          {/* Scanline overlay over entire screen */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, rgba(255,255,255,1) 0px, rgba(255,255,255,1) 1px, transparent 1px, transparent 4px)",
            }}
          />

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-emerald-400/30 bg-gradient-to-br from-black via-zinc-950 to-black p-8 shadow-[0_0_80px_rgba(52,211,153,0.25)] ring-1 ring-emerald-400/20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Corner ticks */}
            <div className="absolute left-3 top-3 h-3 w-3 border-l-2 border-t-2 border-emerald-400/60" />
            <div className="absolute right-3 top-3 h-3 w-3 border-r-2 border-t-2 border-emerald-400/60" />
            <div className="absolute bottom-3 left-3 h-3 w-3 border-b-2 border-l-2 border-amber-400/60" />
            <div className="absolute bottom-3 right-3 h-3 w-3 border-b-2 border-r-2 border-amber-400/60" />

            {/* Close */}
            <button
              onClick={() => dismiss("close")}
              data-testid="shadow-briefing-close"
              className="absolute right-4 top-4 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white/50 transition-colors hover:border-emerald-400/40 hover:text-emerald-200"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>

            {/* Eyebrow */}
            <div className="flex items-center gap-2">
              <span className="relative inline-flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/80" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </span>
              <span className="font-mono text-[10px] font-bold uppercase tracking-[0.32em] text-emerald-300">
                {t.eyebrow}
              </span>
            </div>

            {/* Title */}
            <h2 className="mt-3 font-display text-3xl font-black tracking-tighter text-white sm:text-4xl" data-testid="shadow-briefing-title">
              {t.title}
            </h2>
            <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/55 md:text-base">
              {t.sub}
            </p>

            {/* Codename + invite */}
            <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.04] p-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-amber-300/70">
                  {t.codename_label}
                </p>
                <p className="mt-1.5 font-mono text-2xl font-black tracking-tight text-amber-200" data-testid="shadow-briefing-codename">
                  {profile.operator_codename}
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
                  RANK · RECRUIT · 0 XP
                </p>
              </div>
              <div className="rounded-2xl border border-cyan-400/30 bg-cyan-400/[0.04] p-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-cyan-300/70">
                  {t.invite_label}
                </p>
                <code className="mt-1.5 block truncate font-mono text-xs text-cyan-100">
                  {profile.invite_link}
                </code>
                <button
                  onClick={copyLink}
                  data-testid="shadow-briefing-copy"
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-200 transition-all hover:bg-cyan-400/20"
                >
                  {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copied ? t.copied : t.copy}
                </button>
              </div>
            </div>

            {/* Objectives */}
            <div className="mt-6">
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-emerald-300/70">
                {t.objectives_label}
              </p>
              <ul className="mt-3 space-y-2 font-mono text-xs text-white/60">
                {[t.obj_1, t.obj_2, t.obj_3].map((line, i) => (
                  <li key={i} className="flex items-baseline gap-2">
                    <span className="text-emerald-300">▶</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTAs */}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                onClick={enterNetwork}
                data-testid="shadow-briefing-enter"
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-300 px-5 py-2.5 font-mono text-[10px] font-black uppercase tracking-[0.22em] text-black shadow-[0_0_30px_rgba(251,191,36,0.5)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_50px_rgba(251,191,36,0.8)]"
              >
                <Radar className="h-3.5 w-3.5" />
                {t.cta_enter}
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => dismiss("later")}
                data-testid="shadow-briefing-later"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-white/55 transition-colors hover:text-white"
              >
                {t.cta_later}
              </button>
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.28em] text-white/40">
                <ShieldCheck className="h-3 w-3" />
                {t.classified}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MissionBriefingModal;
